import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Box,
  Grid,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import useAPI from "../../../hooks/useAPI";
import { getSuccessToast, getErrorToast } from "../../../toast";

const today = new Date().toISOString().split("T")[0];
const minDate = "2023-01-01";

const InvoiceTool = ({ user, setInvoiceRecord, setPagination }) => {
  const [mode, setMode] = useState(() => {
    return localStorage.getItem("invoice_mode") || "update";
  });

  const { getAPI, postAPI } = useAPI();
  const [plantOptions, setPlantOptions] = useState([]);

  const [updateData, setUpdateData] = useState({
    party_code: "",
    from_date: today,
    to_date: today,
    plant_code: "",
  });

  const [searchData, setSearchData] = useState({
    party_code: "",
    material: "",
    plant_code: "",
    batch: "",
    invoice_number: "",
    invoice_date: "",
  });

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const plantCodes = user?.user_info?.plant_code_list;
    if (Array.isArray(plantCodes)) {
      setPlantOptions(plantCodes);
    }
  }, [user]);

  const buttonStyle = (active) => ({
    backgroundColor: active ? "#186daa" : "transparent",
    color: active ? "#fff" : "#186daa",
    border: "1px solid #186daa",
    textTransform: "none",
    minWidth: "100px",
    height: "36px",
  });

  const inputStyle = { width: "100%" };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!updateData.party_code) return alert("Party Code is required");

    const filteredData = Object.fromEntries(
      Object.entries(updateData).filter(([_, v]) => v !== "")
    );

    try {
      const response = await postAPI(
        "/api-mankind/invoice-update/",
        filteredData
      );
      if (response?.message) {
        getSuccessToast(response.message);
      } else {
        getErrorToast("Update failed. No message returned.");
      }
    } catch (error) {
      console.error("Update Error:", error);
      getErrorToast(
        error?.response?.data?.message || "Something went wrong during update."
      );
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (
      !searchData.party_code ||
      !searchData.material ||
      !searchData.plant_code
    ) {
      return alert("Party Code, Material, and Plant Code are required");
    }

    const filteredData = Object.fromEntries(
      Object.entries(searchData).filter(([_, v]) => v !== "")
    );

    const queryString = new URLSearchParams(filteredData).toString();

    try {
      const response = await getAPI(
        `/api-mankind/invoice-search/?${queryString}`
      );
      if (response) {
        setInvoiceRecord(response?.results || []);
        setPagination((prev) => ({
          ...prev,
          next: response?.next ?? null,
          previous: response?.previous ?? null,
          count: response?.total_count ?? 0,
          current_page: response?.current_page ?? prev.current_page ?? 1,
          total_pages: response?.total_pages ?? prev.total_pages ?? 1,
        }));
      } else {
        getErrorToast("No results found or invalid response.");
      }
    } catch (error) {
      console.error("Search Error:", error);
      getErrorToast(
        error?.response?.data?.message || "Something went wrong during search."
      );
    }
  };

  const renderInput = (
    label,
    name,
    value,
    onChange,
    required = false,
    type = "text",
    gridWidth = 2.4
  ) => (
    <Grid item xs={12} sm={6} md={gridWidth}>
      <TextField
        label={label}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        size="small"
        sx={inputStyle}
        inputProps={
          type === "date"
            ? {
                min: minDate,
                max: today,
              }
            : {}
        }
      />
    </Grid>
  );

  const handleModeChange = (newMode) => {
    setMode(newMode);
    localStorage.setItem("invoice_mode", newMode);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        maxWidth: "95%",
        mx: "auto",
        p: 3,
        mt: 3,
        borderRadius: 2,
        overflowX: "auto",
      }}
    >
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Button
          variant={mode === "update" ? "contained" : "outlined"}
          onClick={() => handleModeChange("update")}
          sx={buttonStyle(mode === "update")}
        >
          Update
        </Button>
        <Button
          variant={mode === "search" ? "contained" : "outlined"}
          onClick={() => handleModeChange("search")}
          sx={buttonStyle(mode === "search")}
        >
          Search
        </Button>
      </Box>

      <form onSubmit={mode === "update" ? handleUpdate : handleSearch}>
        <Grid container spacing={2} alignItems="center">
          {mode === "update" ? (
            <>
              {renderInput(
                "From",
                "from_date",
                updateData.from_date,
                (v) => setUpdateData({ ...updateData, from_date: v }),
                true,
                "date",
                3
              )}
              {renderInput(
                "To",
                "to_date",
                updateData.to_date,
                (v) => setUpdateData({ ...updateData, to_date: v }),
                true,
                "date",
                3
              )}
              {renderInput(
                "Party Code *",
                "party_code",
                updateData.party_code,
                (v) => setUpdateData({ ...updateData, party_code: v }),
                true,
                "text",
                3
              )}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Plant Code"
                  value={updateData.plant_code}
                  onChange={(e) =>
                    setUpdateData((prev) => ({
                      ...prev,
                      plant_code: e.target.value,
                    }))
                  }
                  SelectProps={{ native: true }}
                  size="small"
                  fullWidth
                >
                  <option value=""></option>
                  {plantOptions.map((plant, idx) => (
                    <option key={idx} value={plant}>
                      {plant}
                    </option>
                  ))}
                </TextField>
              </Grid>
            </>
          ) : (
            <>
              {renderInput(
                "Material *",
                "material",
                searchData.material,
                (v) => setSearchData({ ...searchData, material: v }),
                true,
                "text",
                2.4
              )}
              {renderInput(
                "Batch",
                "batch",
                searchData.batch,
                (v) => setSearchData({ ...searchData, batch: v }),
                false,
                "text",
                2.4
              )}
              {renderInput(
                "Party Code *",
                "party_code",
                searchData.party_code,
                (v) => setSearchData({ ...searchData, party_code: v }),
                true,
                "text",
                2.4
              )}
              {renderInput(
                "Invoice Number",
                "invoice_number",
                searchData.invoice_number,
                (v) => setSearchData({ ...searchData, invoice_number: v }),
                false,
                "text",
                2.4
              )}
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  select
                  label="Plant Code *"
                  value={searchData.plant_code}
                  onChange={(e) =>
                    setSearchData((prev) => ({
                      ...prev,
                      plant_code: e.target.value,
                    }))
                  }
                  SelectProps={{ native: true }}
                  size="small"
                  fullWidth
                  required
                >
                  <option value=""></option>
                  {plantOptions.map((plant, idx) => (
                    <option key={idx} value={plant}>
                      {plant}
                    </option>
                  ))}
                </TextField>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: "#186daa",
                color: "white",
                textTransform: "none",
                height: "36px",
                width: "100%",
              }}
            >
              {mode === "update" ? "Submit Update" : "Submit Search"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default InvoiceTool;
